import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { devtools } from 'zustand/middleware';
import type { ViewerConfig, NormalizedRow, GridAdapter } from '../types/resource';

/**
 * Shared viewer + browse-navigation state. This Zustand store replaces the
 * Angular `$rootScope` slots (`_currentResource`, `_browseIndex`, `_browseApi`):
 * because a detached window opened via window.open() shares the parent JS heap,
 * both the inline and detached viewers read/write this single store — exactly how
 * the Angular child window shared $rootScope (childWindow.ts compiles the child
 * DOM against the parent scope). The grid page registers a GridAdapter here so
 * next()/prev() can page through the ag-Grid infinite model by index.
 */
interface ResourceViewerState {
  isOpen: boolean;
  config: ViewerConfig | null;
  // Browse position — the single source of truth, mirrors _browseIndex.
  browseIndex: number;
  totalRows: number;
  currentResource: NormalizedRow | null;
  // Detached-window state, mirrors $rootScope.viewerDetached.
  detached: boolean;
  // Conversation ("View in Conversation") mode, mirrors $scope.viewInConversation. When on,
  // the left panel becomes the chat message grid. Reset whenever the resource changes.
  viewInConversation: boolean;
  // Live handle to the active grid's infinite model (mirrors _browseApi).
  gridAdapter: GridAdapter | null;
  // Unsaved redaction / note flags, mirrors $scope.pendingChanges. Checked before
  // Next/Previous/Close.
  pendingChanges: PendingChanges;
  // Bumped by disregardChanges so components can drop their unsaved input.
  disregardCount: number;

  setPendingChange: (kind: keyof PendingChanges, value: boolean) => void;
  /** Clears both flags (Angular's disregardChanges). */
  disregardChanges: () => void;

  setGridAdapter: (adapter: GridAdapter | null) => void;
  enterConversation: () => void;
  exitConversation: () => void;
  setTotalRows: (total: number) => void;
  /**
   * Open a resource by id, outside the browse set (related-doc / conversation /
   * analytics cross-links, and the Angular bridge). Mirrors Angular's grid-less
   * LaunchFullView: shows the resource without changing browse position.
   */
  open: (config: ViewerConfig) => void;
  /** Open from a grid row-click: adopt the clicked index + row. */
  openAt: (index: number, row: NormalizedRow) => void;
  /** Programmatic navigation (mirrors gridNextRow/gridPreviousRow). */
  next: () => Promise<void>;
  prev: () => Promise<void>;
  /** Move to the detached window. `win` must be opened synchronously in the click
   *  handler (see openDetachedWindow) so the popup blocker treats it as user-initiated. */
  detach: (win: Window) => void;
  popIn: () => void;
  close: () => void;
}

export interface PendingChanges {
  redaction: boolean;
  notes: boolean;
}

// The detached window is a live host object, kept outside the (immer) state tree.
// `detached` in state drives rendering; this holds the actual window to portal into
// and to close on pop-in.
let detachedWin: Window | null = null;
export function getDetachedWindow(): Window | null {
  return detachedWin;
}
function closeDetachedWindow(): void {
  if (detachedWin) {
    try { detachedWin.close(); } catch { /* already closed */ }
    detachedWin = null;
  }
}

function rowToConfig(row: NormalizedRow): ViewerConfig {
  return { projectId: row.projectId, collectionId: row.collectionId, resourceId: row.id };
}

export const useResourceViewerStore = create<ResourceViewerState>()(
  devtools(
    immer((set, get) => ({
      isOpen: false,
      config: null,
      browseIndex: 0,
      totalRows: 0,
      currentResource: null,
      detached: false,
      viewInConversation: false,
      gridAdapter: null,
      pendingChanges: { redaction: false, notes: false },
      disregardCount: 0,

      setPendingChange: (kind, value) =>
        set((state) => {
          state.pendingChanges[kind] = value;
        }),

      disregardChanges: () =>
        set((state) => {
          state.pendingChanges = { redaction: false, notes: false };
          state.disregardCount += 1;
        }),

      setGridAdapter: (adapter) =>
        set((state) => {
          state.gridAdapter = adapter as GridAdapter | null;
          if (adapter) state.totalRows = adapter.getRowCount();
        }),

      enterConversation: () =>
        set((state) => {
          state.viewInConversation = true;
        }),

      exitConversation: () =>
        set((state) => {
          state.viewInConversation = false;
        }),

      setTotalRows: (total) =>
        set((state) => {
          state.totalRows = total;
        }),

      open: (config) =>
        set((state) => {
          state.isOpen = true;
          state.config = config;
          // Cross-link open is not a browse-set navigation; leave browseIndex as-is
          // but drop the stale row so the viewer resolves the new resource by id.
          state.currentResource = null;
          state.viewInConversation = false;
        }),

      openAt: (index, row) =>
        set((state) => {
          state.isOpen = true;
          state.browseIndex = index;
          state.currentResource = row;
          state.config = rowToConfig(row);
          state.viewInConversation = false;
        }),

      // Bump the index, then resolve the target row from the grid (loading its
      // block if needed) before swapping the viewer — mirrors gridNavigateRows.
      next: async () => {
        const { browseIndex, totalRows, gridAdapter } = get();
        if (!gridAdapter || browseIndex >= totalRows - 1) return;
        await navigateTo(set, gridAdapter, browseIndex + 1);
      },

      prev: async () => {
        const { browseIndex, gridAdapter } = get();
        if (!gridAdapter || browseIndex <= 0) return;
        await navigateTo(set, gridAdapter, browseIndex - 1);
      },

      detach: (win) => {
        detachedWin = win;
        set((state) => {
          state.detached = true;
        });
      },

      popIn: () => {
        closeDetachedWindow();
        set((state) => {
          state.detached = false;
        });
      },

      close: () => {
        closeDetachedWindow();
        set((state) => {
          state.isOpen = false;
          state.config = null;
          state.currentResource = null;
          state.detached = false;
          state.viewInConversation = false;
        });
      },
    })),
    { name: 'ResourceViewerStore' }
  )
);

async function navigateTo(
  set: (fn: (state: ResourceViewerState) => void) => void,
  adapter: GridAdapter,
  index: number
): Promise<void> {
  const row = await adapter.ensureIndexLoaded(index);
  if (!row) return;
  adapter.focus(index);
  set((state) => {
    state.browseIndex = index;
    state.currentResource = row;
    state.config = { projectId: row.projectId, collectionId: row.collectionId, resourceId: row.id };
    // A new document exits conversation mode (mirrors Angular's per-resource re-init).
    state.viewInConversation = false;
  });
}
